import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../../hooks/http.hook';
import '../../dashboard/Dashboard.css';
import '../../shipment/ShipmentsPage.css';
import '../OrdersPage.css';
import CreateBusinessHeader from '../../business/components/CreateBusinessHeader';
import { PaymentStatusTypes } from '../../../types/PaymentStatusTypes';

const PAYMENT_LABELS = PaymentStatusTypes.labels;

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function DebtsPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { requestWithMeta } = useHttp();

  const [debts, setDebts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchDebts = React.useCallback(async () => {
    if (!branchId || branchId === 'store') {
      setDebts([]);
      setLoading(false);
      setError('Выберите филиал');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await requestWithMeta(
        `/operator/branches/${encodeURIComponent(branchId)}/debts`,
        'GET'
      );
      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить долги');
        setDebts([]);
        return;
      }
      const list = Array.isArray(res?.data?.debts) ? res.data.debts : [];
      setDebts(list);
    } catch (_) {
      setError('Ошибка загрузки');
      setDebts([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, requestWithMeta]);

  React.useEffect(() => {
    fetchDebts();
  }, [fetchDebts]);

  const handleBack = () => {
    navigate(`/branch/${branchId || 'store'}`);
  };

  const handleOpenOrder = (order) => {
    const id = order?.id ?? order?._id;
    if (id == null) return;
    navigate(`/branch/${branchId}/orders/${encodeURIComponent(id)}`, {
      state: {
        backTo: {
          pathname: `/branch/${branchId}/debts`,
          search: '',
        },
      },
    });
  };

  return (
    <div className="shipments-page-root">
      <CreateBusinessHeader
        title="Долги"
        subtitle=""
        onBack={handleBack}
      />

      <main className="shipments-page-content">
        {loading ? (
          <div className="shipments-page-loading" aria-busy="true">
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
          </div>
        ) : error ? (
          <div className="shipments-page-error">{error}</div>
        ) : (
          <>
            <div className="main-page-sectionHeader shipments-page-sectionHeader">
              <div className="main-page-sectionSub">
                <span>
                  {debts.length
                    ? `${debts.length} ${
                        debts.length === 1
                          ? 'долг'
                          : debts.length >= 2 && debts.length <= 4
                            ? 'долга'
                            : 'долгов'
                      }`
                    : 'Нет долгов'}
                </span>
              </div>
            </div>

            {debts.length === 0 ? (
              <div className="shipments-page-empty">Нет долгов</div>
            ) : (
              <ul className="shipments-page-list" aria-label="Список долгов">
                {debts.map((order) => {
                  const id = order?.id ?? order?._id;
                  const customerName = order?.customerName ?? order?.customer_name ?? '—';
                  const paymentStatusId =
                    order?.paymentStatusId ?? order?.payment_status_id ?? PaymentStatusTypes.NOT_PAID;
                  const paymentLabel =
                    PAYMENT_LABELS[paymentStatusId] ?? `Оплата ${paymentStatusId}`;

                  return (
                    <li key={id} className="shipments-page-item">
                      <div className="shipments-page-itemRow">
                        <button
                          type="button"
                          className="shipments-page-itemMain"
                          onClick={() => handleOpenOrder(order)}
                          aria-label={`Открыть заказ № ${id}`}
                        >
                          <span className="shipments-page-itemName">Заказ № {id}</span>
                          <span className="shipments-page-itemPrice">{customerName}</span>
                          <span className={`orders-page-itemPayment orders-page-itemPayment--${paymentStatusId}`}>
                            {paymentLabel}
                          </span>
                        </button>
                        <span className="orders-page-itemTotal">
                          {formatMoney(order?.debt)} сом
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default DebtsPage;
